import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Sparkles, Zap, Heart, Share2, Building2, Users, User, Menu, X, Check, ArrowRight, 
  Search, ChevronLeft, ChevronRight, LogOut, Star, ShieldCheck, TrendingUp, 
  Camera, Upload, Palette, DollarSign, BarChart2, Eye, Download, Store,
  Shirt, MessageSquare, Award, Target, Globe, Layers
} from 'lucide-react';
import DemoVideoModal from '../components/DemoVideoModal';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [showDemo, setShowDemo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [featuredCollabs, setFeaturedCollabs] = useState([]);
  const [collabsLoading, setCollabsLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [previewState, setPreviewState] = useState('before');
  const [activeUserType, setActiveUserType] = useState('brand');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchFeaturedCollaborations();
  }, [isAuthenticated]);

  if (isAuthenticated) {
    return null;
  }

  const fetchFeaturedCollaborations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/featured-collaborations?limit=12`);
      if (res.ok) {
        const data = await res.json();
        setFeaturedCollabs(data.collaborations || []);
      }
    } catch (err) {
      console.error('Error fetching featured collaborations:', err);
    } finally {
      setCollabsLoading(false);
    }
  };

  const nextSlide = () => {
    const maxIndex = Math.max(0, featuredCollabs.length - 4);
    setCarouselIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const prevSlide = () => {
    setCarouselIndex(prev => Math.max(prev - 1, 0));
  };

  const plans = [
    {
      id: 'brands',
      name: 'Brands',
      icon: Building2,
      price: '$99 - $399/month',
      color: 'blue',
      features: ['200-1000 AI try-ons/month', 'Upload 25-500 clothing items', 'Reduce returns by 30-50%', 'Connect with influencers', 'Analytics & ROI tracking']
    },
    {
      id: 'influencers',
      name: 'Influencers',
      icon: Users,
      price: '$19 - $79.99/month',
      color: 'purple',
      features: ['50-200 AI try-ons/month', 'Upload 20-100 looks', 'Personal profile page', 'Earn through brand campaigns', 'Brand collaborations']
    },
    {
      id: 'personal',
      name: 'Personal',
      icon: User,
      price: '$9/month',
      color: 'rose',
      features: ['20 AI try-ons/month', 'Try before you buy', 'Download your looks', 'Personal use']
    }
  ];

  const userTypeSteps = {
    brand: {
      label: 'For Brands',
      color: 'blue',
      icon: Building2,
      steps: [
        { icon: Upload, title: 'Upload Your Catalog', desc: 'Add your clothing items with photos. Build a portfolio that influencers and customers can browse.' },
        { icon: Users, title: 'Connect with Influencers', desc: 'Launch campaigns and collaborate with influencers who showcase your products with AI try-on.' },
        { icon: BarChart2, title: 'Reduce Returns, Boost Sales', desc: 'Track ROI as customers try before they buy. Reduce returns by up to 50% and save on shipping.' }
      ]
    },
    influencer: {
      label: 'For Influencers',
      color: 'purple',
      icon: Users,
      steps: [
        { icon: Camera, title: 'Build Your Portfolio', desc: 'Create stunning look portfolios with AI-generated try-on images. Showcase your personal style.' },
        { icon: MessageSquare, title: 'Land Brand Deals', desc: 'Get discovered by brands. Accept campaigns, create content, and collaborate directly in-app.' },
        { icon: DollarSign, title: 'Earn & Grow', desc: 'Get paid through funded campaigns. Track your earnings, link clicks, and profile engagement.' }
      ]
    },
    personal: {
      label: 'For Shoppers',
      color: 'rose',
      icon: User,
      steps: [
        { icon: Camera, title: 'Snap Your Photo', desc: 'Take a quick full-body photo or use one you already have. Works in any outfit.' },
        { icon: Shirt, title: 'Try Any Outfit', desc: 'Upload clothing images from any store or brand. See exactly how it looks on you before buying.' },
        { icon: Download, title: 'Shop with Confidence', desc: 'Save your favorite looks, compare outfits side by side, and never second-guess a purchase again.' }
      ]
    }
  };

  const testimonials = [
    {
      name: 'Urban Style Co.',
      handle: '@urbanstyle',
      type: 'Brand',
      avatar: 'US',
      color: 'from-blue-500 to-cyan-500',
      quote: 'VirtualFit reduced our return rate by 38% in the first quarter. The influencer collaboration tools brought us 3x more engagement than traditional campaigns.',
      rating: 5
    },
    {
      name: 'Fashion Jane',
      handle: '@fashionjane',
      type: 'Influencer',
      avatar: 'FJ',
      color: 'from-purple-500 to-pink-500',
      quote: "I've earned more through VirtualFit campaigns in 2 months than 6 months of traditional sponsorships. Brands love that I can showcase their clothes with AI try-on.",
      rating: 5
    },
    {
      name: 'Chic Boutique',
      handle: '@chicboutique',
      type: 'Brand',
      avatar: 'CB',
      color: 'from-emerald-500 to-teal-500',
      quote: 'The virtual try-on technology is incredible. Our customers make better purchasing decisions, and our shipping costs dropped by 30%.',
      rating: 5
    },
    {
      name: 'Trendy Wear',
      handle: '@trendywear',
      type: 'Brand',
      avatar: 'TW',
      color: 'from-orange-500 to-amber-500',
      quote: "We connected with 12 influencers in our first month. The campaign management and content approval workflow is seamless. Game changer for our brand.",
      rating: 5
    }
  ];

  const brandLogos = [
    { name: 'Urban Style Co.', handle: 'urbanstyle' },
    { name: 'Trendy Wear', handle: 'trendywear' },
    { name: 'Chic Boutique', handle: 'chicboutique' },
    { name: 'Fashion Jane', handle: 'fashionjane' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50">
      <DemoVideoModal isOpen={showDemo} onClose={() => setShowDemo(false)} />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center space-x-2">
              <Sparkles className="w-8 h-8 text-rose-400" />
              <span className="text-2xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
                VirtualFit
              </span>
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">Home</Link>
              <Link to="/search" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">Discover Brands & Influencers</Link>
              <Link to="/blog" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">Blog</Link>
              <Link to="/about" className="text-gray-700 hover:text-rose-500 font-medium transition-colors">About</Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <span className="text-sm text-gray-600">Hi, {user?.name?.split(' ')[0]}</span>
                  <Link
                    to="/dashboard"
                    className="px-6 py-2.5 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    Dashboard
                  </Link>
                  <button 
                    onClick={logout}
                    className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-gray-100"
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 hover:text-rose-500 font-medium">Login</Link>
                  <Link
                    to="/subscribe"
                    className="px-6 py-2.5 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    Subscribe Now
                  </Link>
                </>
              )}
            </div>
            
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col space-y-4">
                <Link to="/" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                <Link to="/search" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Discover Brands & Influencers</Link>
                <Link to="/blog" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                <Link to="/about" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                    <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-left text-gray-700 hover:text-rose-500 font-medium">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                    <Link to="/subscribe" className="px-6 py-2.5 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-full font-semibold text-center" onClick={() => setMobileMenuOpen(false)}>
                      Subscribe Now
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* ============================== */}
      {/* HERO SECTION - Enhanced */}
      {/* ============================== */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-rose-100 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-rose-500 mr-2" />
              <span className="text-rose-700 font-medium">AI-Powered Virtual Try-On Platform</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 leading-tight">
              Try Clothes Virtually.
              <br />
              <span className="bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">See How They Look Before You Buy</span>
            </h1>
            
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto mb-10">
              Brands reduce returns by 50%. Influencers earn through campaigns. Shoppers try before they buy. All powered by AI.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/try"
                className="px-8 py-4 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-xl font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center justify-center"
                data-testid="hero-free-trial-btn"
              >
                Try Your First Look Free <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <button
                onClick={() => setShowDemo(true)}
                className="px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold text-lg hover:shadow-lg transition-all border border-gray-200"
                data-testid="hero-watch-demo-btn"
              >
                Watch Demo
              </button>
            </div>

            <p className="text-sm text-gray-400 mt-4">No credit card required. 2 free AI try-on images.</p>
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* BRAND LOGO STRIP */}
      {/* ============================== */}
      <section className="py-8 border-y border-gray-200/60 bg-white/40" data-testid="brand-logo-strip">
        <div className="container mx-auto max-w-5xl px-6">
          <p className="text-center text-sm text-gray-400 font-medium uppercase tracking-wider mb-6">Trusted by brands and influencers on VirtualFit</p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {brandLogos.map((brand) => (
              <Link
                key={brand.handle}
                to={`/search`}
                className="text-xl md:text-2xl font-bold text-gray-300 hover:text-gray-500 transition-colors tracking-tight"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                {brand.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* STATS / NUMBERS BAR */}
      {/* ============================== */}
      <section className="py-14 bg-white/50" data-testid="stats-section">
        <div className="container mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-rose-500">500+</p>
              <p className="text-gray-600 mt-1 text-sm">Try-Ons Generated</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-rose-500">50+</p>
              <p className="text-gray-600 mt-1 text-sm">Brands & Influencers</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-rose-500">40-50%</p>
              <p className="text-gray-600 mt-1 text-sm">Return Reduction</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-rose-500">3x</p>
              <p className="text-gray-600 mt-1 text-sm">Higher Conversion</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* LIVE PREVIEW / BEFORE-AFTER */}
      {/* ============================== */}
      <section className="py-20 px-6" data-testid="live-preview-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-rose-100 text-rose-600 rounded-full text-sm font-medium mb-4">
              <Eye className="w-4 h-4 inline mr-1" />
              See It In Action
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">AI Try-On That Looks Real</h2>
            <p className="text-base text-gray-600 max-w-xl mx-auto">
              Upload your photo. Upload the clothing. Our AI creates a realistic try-on in seconds.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Toggle Buttons */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setPreviewState('before')}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    previewState === 'before'
                      ? 'bg-white text-gray-800 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="preview-before-btn"
                >
                  Before
                </button>
                <button
                  onClick={() => setPreviewState('after')}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                    previewState === 'after'
                      ? 'bg-gradient-to-r from-rose-400 to-orange-400 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="preview-after-btn"
                >
                  After AI Try-On
                </button>
              </div>
            </div>

            {/* Before/After Display */}
            <div className="grid md:grid-cols-3 gap-6 items-center">
              {/* Person Photo */}
              <div className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-500 ${
                previewState === 'before' ? 'ring-2 ring-rose-300 scale-[1.02]' : 'opacity-70 scale-95'
              }`}>
                <div className="p-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_df032d4f-ec42-4adf-9d2c-5bdf276efdc9/artifacts/oqmtbsop_Your%20Image.png"
                    alt="Your photo"
                    className="w-full h-72 object-contain rounded-xl"
                  />
                </div>
                <div className="px-4 pb-4 text-center">
                  <p className="text-sm font-semibold text-gray-700">Your Photo</p>
                </div>
              </div>

              {/* Plus / Arrow */}
              <div className="hidden md:flex flex-col items-center justify-center">
                <div className={`transition-all duration-500 ${previewState === 'after' ? 'scale-110' : ''}`}>
                  {previewState === 'before' ? (
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-2xl text-gray-400 font-light">+</div>
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-r from-rose-400 to-orange-400 flex items-center justify-center">
                      <ArrowRight className="w-6 h-6 text-white" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">{previewState === 'before' ? 'Select clothing' : 'AI Generated'}</p>
              </div>

              {/* Clothing / Result */}
              <div className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-500 ${
                previewState === 'after' ? 'ring-2 ring-rose-300 scale-[1.02]' : ''
              }`}>
                <div className="p-3">
                  <img 
                    src={previewState === 'before' 
                      ? "https://customer-assets.emergentagent.com/job_df032d4f-ec42-4adf-9d2c-5bdf276efdc9/artifacts/oynpzo5v_the%20clothing.png"
                      : "https://customer-assets.emergentagent.com/job_df032d4f-ec42-4adf-9d2c-5bdf276efdc9/artifacts/dxlc2f0w_Generated%20image.png"
                    }
                    alt={previewState === 'before' ? "The clothing" : "AI generated try-on result"}
                    className="w-full h-72 object-contain rounded-xl"
                  />
                </div>
                <div className="px-4 pb-4 text-center">
                  <p className="text-sm font-semibold text-gray-700">
                    {previewState === 'before' ? 'The Clothing' : 'AI Try-On Result'}
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-10">
              <Link
                to="/try"
                className="inline-flex items-center px-8 py-3.5 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-xl font-semibold hover:shadow-xl transition-all transform hover:scale-105"
                data-testid="preview-cta-btn"
              >
                Try It Yourself  &mdash;  Free <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* HOW IT WORKS (ORIGINAL - KEPT) */}
      {/* ============================== */}
      <section className="py-20 px-6" style={{ backgroundColor: '#FFF8F0' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Georgia, serif' }}>How It Works</h2>
            <p className="text-base text-gray-600">Three simple steps to see your perfect look</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {/* Step 1 - Upload Your Photo */}
            <div className="text-center">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-4">
                  <div className="rounded-xl overflow-hidden h-72 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_df032d4f-ec42-4adf-9d2c-5bdf276efdc9/artifacts/oqmtbsop_Your%20Image.png" 
                      alt="Upload your photo - woman in white t-shirt and jeans"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Upload Your Photo</h3>
                  <p className="text-gray-600 text-sm">Take or upload a full-body photo of yourself. Our AI works best with clear, well-lit images.</p>
                </div>
              </div>
            </div>
            
            {/* Step 2 - Choose Clothing */}
            <div className="text-center">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-4">
                  <div className="rounded-xl overflow-hidden h-72 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_df032d4f-ec42-4adf-9d2c-5bdf276efdc9/artifacts/oynpzo5v_the%20clothing.png" 
                      alt="Choose clothing - red floral dress"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Choose Clothing</h3>
                  <p className="text-gray-600 text-sm">Upload a clothing image or paste a link from your favorite store. Select your size and fit preferences.</p>
                </div>
              </div>
            </div>
            
            {/* Step 3 - See Your Look */}
            <div className="text-center">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-4">
                  <div className="rounded-xl overflow-hidden h-72 flex items-center justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_df032d4f-ec42-4adf-9d2c-5bdf276efdc9/artifacts/dxlc2f0w_Generated%20image.png" 
                      alt="See your look - woman wearing the red floral dress"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">See Your Look</h3>
                  <p className="text-gray-600 text-sm">Get your AI-generated try-on instantly. Save to gallery, share with friends, or shop the look!</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <Link 
              to="/try"
              data-testid="how-it-works-cta"
              className="inline-flex items-center px-10 py-4 text-white rounded-full font-semibold text-lg hover:shadow-xl transition-all transform hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #F97316 0%, #EC4899 100%)' }}
            >
              Try It Now - 2 Free Images! <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* HOW IT WORKS - PER USER TYPE */}
      {/* ============================== */}
      <section className="py-20 px-6 bg-white overflow-hidden" data-testid="how-it-works-per-type">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Built For Everyone</h2>
            <p className="text-base text-gray-600">Whether you're a brand, influencer, or shopper  &mdash;  VirtualFit works for you</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-gray-100 rounded-2xl p-1.5 gap-1">
              {Object.entries(userTypeSteps).map(([key, val]) => {
                const Icon = val.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveUserType(key)}
                    className={`flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      activeUserType === key
                        ? key === 'brand' ? 'bg-blue-500 text-white shadow-md' 
                          : key === 'influencer' ? 'bg-purple-500 text-white shadow-md' 
                          : 'bg-rose-500 text-white shadow-md'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'
                    }`}
                    data-testid={`user-type-tab-${key}`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {val.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Steps for Active Type */}
          <div className="grid md:grid-cols-3 gap-8">
            {userTypeSteps[activeUserType].steps.map((step, idx) => {
              const StepIcon = step.icon;
              const colorClass = activeUserType === 'brand' ? 'blue' : activeUserType === 'influencer' ? 'purple' : 'rose';
              return (
                <div key={idx}>
                  <div className="bg-gray-50 rounded-2xl p-8 h-full hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-5">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${
                        colorClass === 'blue' ? 'bg-blue-500' : colorClass === 'purple' ? 'bg-purple-500' : 'bg-rose-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        colorClass === 'blue' ? 'bg-blue-100' : colorClass === 'purple' ? 'bg-purple-100' : 'bg-rose-100'
                      }`}>
                        <StepIcon className={`w-6 h-6 ${
                          colorClass === 'blue' ? 'text-blue-500' : colorClass === 'purple' ? 'text-purple-500' : 'text-rose-500'
                        }`} />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to={`/subscribe?type=${activeUserType === 'brand' ? 'brand' : activeUserType === 'influencer' ? 'influencer' : 'personal'}`}
              className={`inline-flex items-center px-8 py-3.5 text-white rounded-xl font-semibold hover:shadow-xl transition-all transform hover:scale-105 ${
                activeUserType === 'brand' ? 'bg-blue-500 hover:bg-blue-600' 
                : activeUserType === 'influencer' ? 'bg-purple-500 hover:bg-purple-600' 
                : 'bg-rose-500 hover:bg-rose-600'
              }`}
              data-testid="user-type-cta"
            >
              Get Started as {activeUserType === 'brand' ? 'a Brand' : activeUserType === 'influencer' ? 'an Influencer' : 'a Shopper'} <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* PLANS SECTION (ORIGINAL) */}
      {/* ============================== */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Choose Your Plan</h2>
            <p className="text-base text-gray-600">Built for every use case</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => {
              const Icon = plan.icon;
              const isSelected = selectedPlan === plan.id;
              
              return (
                <div 
                  key={plan.name}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`bg-white rounded-2xl p-8 shadow-lg transition-all duration-200 relative cursor-pointer flex flex-col ${
                    isSelected
                      ? 'ring-2 ring-rose-500 shadow-xl scale-[1.02]' 
                      : 'hover:shadow-xl hover:ring-2 hover:ring-gray-200'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-rose-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Selected
                    </div>
                  )}
                  
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${
                    plan.color === 'blue' ? 'bg-blue-100' :
                    plan.color === 'purple' ? 'bg-purple-100' : 'bg-rose-100'
                  }`}>
                    <Icon className={`w-7 h-7 ${
                      plan.color === 'blue' ? 'text-blue-500' :
                      plan.color === 'purple' ? 'text-purple-500' : 'text-rose-500'
                    }`} />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                  <p className="text-rose-500 font-semibold mb-6">{plan.price}</p>
                  
                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-500" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to={`/subscribe?type=${plan.id === 'brands' ? 'brand' : plan.id === 'influencers' ? 'influencer' : 'personal'}`}
                    onClick={(e) => e.stopPropagation()}
                    className={`block w-full py-3 text-center rounded-xl font-semibold transition-all mt-auto ${
                      isSelected
                        ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white'
                        : 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-gradient-to-r hover:from-rose-500 hover:to-orange-500 hover:text-white hover:border-transparent'
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* COLLABORATION HIGHLIGHTS (ORIGINAL) */}
      {/* ============================== */}
      {featuredCollabs.length > 0 && (
        <section className="py-20 px-6 bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-rose-100 text-rose-600 rounded-full text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4 inline mr-1" />
                Fresh Collaborations
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Collaboration Highlights
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                See how influencers are styling brand products with VirtualFit's AI try-on technology
              </p>
            </div>

            {/* Carousel Container */}
            <div className="relative">
              {/* Navigation Arrows */}
              {carouselIndex > 0 && (
                <button 
                  onClick={prevSlide}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                  data-testid="carousel-prev"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
              )}
              {carouselIndex < featuredCollabs.length - 4 && (
                <button 
                  onClick={nextSlide}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
                  data-testid="carousel-next"
                >
                  <ChevronRight className="w-6 h-6 text-gray-600" />
                </button>
              )}

              {/* Cards Grid */}
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-300 ease-in-out gap-6"
                  style={{ transform: `translateX(-${carouselIndex * (100 / 4 + 1.5)}%)` }}
                >
                  {featuredCollabs.map((collab) => {
                    const productHref = collab.product_url && !/^https?:\/\//i.test(collab.product_url) 
                      ? `https://${collab.product_url}` 
                      : collab.product_url;
                    return (
                    <a
                      key={collab.id}
                      href={productHref || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-none w-full md:w-[calc(25%-18px)] bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                      data-testid={`collab-card-${collab.id}`}
                      onClick={(e) => { if (!productHref) e.preventDefault(); }}
                    >
                      <div className="relative">
                        <img 
                          src={collab.image_url 
                            ? collab.image_url
                            : collab.image_base64?.startsWith('data:') 
                              ? collab.image_base64 
                              : `data:image/jpeg;base64,${collab.image_base64}`}
                          alt={collab.product_name}
                          className="w-full h-64 object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                          }}
                        />
                        {/* Brand Badge */}
                        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
                          <span className="text-sm font-medium text-gray-700">@{collab.brand_handle}</span>
                        </div>
                        {/* Shop Now Badge */}
                        <div className="absolute top-3 right-3 bg-rose-500 text-white px-3 py-1 rounded-full shadow-sm text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          Shop Now →
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          {collab.influencer_image ? (
                            <img 
                              src={collab.influencer_image.startsWith('data:') 
                                ? collab.influencer_image 
                                : collab.influencer_image.startsWith('http')
                                  ? collab.influencer_image
                                  : `data:image/jpeg;base64,${collab.influencer_image}`}
                              alt={collab.influencer_name}
                              className="w-8 h-8 rounded-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-rose-400 to-orange-400 flex items-center justify-center">
                              <Users className="w-4 h-4 text-white" />
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-800 truncate">@{collab.influencer_handle}</span>
                        </div>
                        <p className="text-xs text-rose-500 font-medium mb-1 truncate">{collab.product_name}</p>
                        <p className="text-sm text-gray-600 line-clamp-2 break-all">{collab.caption || 'Check out this look!'}</p>
                      </div>
                    </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Carousel Dots */}
            {featuredCollabs.length > 4 && (
              <div className="flex justify-center space-x-2 mt-8">
                {Array.from({ length: Math.ceil(featuredCollabs.length / 4) }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCarouselIndex(idx * 4)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      Math.floor(carouselIndex / 4) === idx ? 'bg-rose-500' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* CTA Section */}
            <div className="text-center mt-10">
              <p className="text-gray-600 mb-4">Want your content featured here?</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/subscribe?type=influencer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-rose-50 text-rose-600 rounded-full font-medium border border-rose-200 hover:bg-rose-500 hover:text-white hover:border-rose-500 transition-all duration-200"
                >
                  Join as Influencer
                </Link>
                <Link
                  to="/subscribe?type=brand"
                  className="inline-flex items-center justify-center px-6 py-3 bg-orange-50 text-orange-600 rounded-full font-medium border border-orange-200 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-all duration-200"
                >
                  Partner as Brand
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============================== */}
      {/* TESTIMONIALS / SOCIAL PROOF */}
      {/* ============================== */}
      <section className="py-20 px-6 bg-white" data-testid="testimonials-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
              <Star className="w-4 h-4 inline mr-1" />
              What Our Users Say
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Loved by Brands & Influencers</h2>
            <p className="text-base text-gray-600 max-w-xl mx-auto">
              See why businesses and creators choose VirtualFit to grow their revenue
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                className="bg-gray-50 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-shadow relative"
                data-testid={`testimonial-${idx}`}
              >
                {/* Quote mark */}
                <div className="absolute top-4 right-6 text-6xl text-gray-200 font-serif leading-none select-none">"</div>
                
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-gray-700 mb-6 leading-relaxed relative z-10">"{t.quote}"</p>

                {/* Author */}
                <div className="flex items-center">
                  <div className={`w-11 h-11 rounded-full bg-gradient-to-r ${t.color} flex items-center justify-center text-white font-bold text-sm`}>
                    {t.avatar}
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-gray-800 text-sm">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.handle} · {t.type}</p>
                  </div>
                  <div className="ml-auto">
                    <ShieldCheck className="w-5 h-5 text-green-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== */}
      {/* FINAL CTA - Enhanced */}
      {/* ============================== */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-r from-rose-400 to-orange-400 rounded-3xl p-10 md:p-14 text-center text-white relative overflow-hidden">
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Try Your First Look Free</h2>
              <p className="text-lg opacity-90 mb-8 max-w-lg mx-auto">
                No credit card needed. Upload your photo, choose a clothing item, and see the magic in seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/try"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-rose-500 rounded-xl font-semibold text-lg hover:shadow-xl transition-all"
                  data-testid="final-cta-free-trial"
                >
                  Start Free Try-On <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/subscribe"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/20 text-white rounded-xl font-semibold text-lg hover:bg-white/30 transition-all border border-white/30"
                  data-testid="final-cta-subscribe"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer isAuthenticated={isAuthenticated} />
    </div>
  );
};

export default LandingPage;
